<template>
  <section class="presence presence--us" ref="root" aria-label="Dónde trabajamos en USA">
    <!-- Encabezado -->
    <header class="presence__head" ref="headRef">
      <h2 class="presence__title">
        Nuestro <span class="mark">alcance en USA</span>
      </h2>
      <p class="presence__sub">
        Zonas donde ya hemos trabajado y seguimos atendiendo. Atención en español e inglés.
      </p>
    </header>

    <!-- Lienzo mapa -->
    <div
      class="map"
      ref="mapRef"
      :style="{ '--ratio': mapRatio }"
      @click="handleDevClick"
    >
      <img
        class="map__img"
        :src="mapSrcUs"
        alt="Mapa de Estados Unidos"
        ref="imgRef"
        @load="onImageLoad"
      />

      <!-- Pines (SIEMPRE muestran etiqueta) -->
      <button
        v-for="(p, i) in pinsUs"
        :key="p.id || i"
        class="pin"
        :style="pinStyle(p)"
        :aria-label="p.label"
        @mouseenter="hoverPin(i)"
        @mouseleave="leavePin(i)"
        @click="openAction(p)"
        :ref="el => pinEls[i] = el"
      >
        <span class="pin__dot"></span>
        <span class="pin__pulse"></span>
        <span class="pin__label" :class="{ 'pin__label--left': p.labelSide === 'left' }">
          {{ p.label }}
          <small v-if="p.note">{{ p.note }}</small>
        </span>
      </button>

      <!-- Grid/coords para ajustar posiciones (solo si devMode) -->
      <div v-if="devMode" class="dev-grid" aria-hidden="true"></div>
      <div v-if="devMode && lastClick" class="dev-coords">
        {{ lastClick.left.toFixed(2) }}%, {{ lastClick.top.toFixed(2) }}%
      </div>
    </div>

    <!-- Franja de confianza -->
    <ul class="trustbar" ref="trustRef">
      <li>✅ Empresa registrada en USA</li>
      <li>🛟 Soporte 24/7</li>
      <li>📈 +120 proyectos entregados</li>
      <li>💬 Atención en ES / EN</li>
      <li>🧾 Cotización gratis</li>
    </ul>

    <!-- CTA -->
  
    <!-- Toggle DEV -->
    <label class="dev-toggle">
      <input type="checkbox" v-model="devMode" />
      Modo dev (ver grilla y coords)
    </label>
  </section>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'MapaUSA',
  props: {
    mapUsSrcProp: { type: String, default: '' }, // opcional; si no viene, usamos /assets/us.svg
    waPhone:  { type: String, default: '+16122619287' },
    waText:   { type: String, default: 'Hola, vi su alcance en USA y quiero una cotización.' }
  },
  setup (props) {
    const root = ref(null)
    const headRef = ref(null)
    const mapRef = ref(null)
    const imgRef = ref(null)
    const trustRef = ref(null)
    const pinEls = ref([])

    const devMode = ref(false)
    const lastClick = ref(null)

    // Imagen del mapa (desde /src/assets)
    const mapSrcUs = computed(() => props.mapUsSrcProp || new URL('@/assets/us.svg', import.meta.url).href)

    // Mantener relación del mapa según su natural size
    const mapNatural = ref({ w: 16, h: 9 })
    const mapRatio = computed(() => `${(mapNatural.value.h / mapNatural.value.w) * 100}%`)

    // Contacto
    const waHref = computed(() => {
      const num = props.waPhone.replace(/\D/g, '')
      return `https://wa.me/${num}?text=${encodeURIComponent(props.waText)}`
    })
    const telHref = computed(() => `tel:${props.waPhone.replace(/\D/g, '')}`)

    // Pines (coordenadas en % del contenedor)
    const pinsUs = ref([
      { id:'msp',   label:'Minneapolis–St. Paul, MN', left:60.8, top:26.2, note:'Base',  labelSide:'right', weight:'lead' },
      { id:'stc',   label:'St. Cloud, MN',            left:58.8, top:24.2, labelSide:'right' },
      { id:'roch',  label:'Rochester, MN',            left:62.6, top:29.2, labelSide:'right' },
      { id:'mia',   label:'Miami, FL',                left:76.8, top:74.6, labelSide:'left',  weight:'lead' },
      { id:'orl',   label:'Orlando, FL',              left:73.7, top:67.1, labelSide:'left' },
      { id:'rdu',   label:'Raleigh, NC',              left:74.0, top:55.0, labelSide:'left' },
      { id:'clt',   label:'Charlotte, NC',            left:72.0, top:57.5, labelSide:'left' },
      { id:'pdx',   label:'Portland, OR',             left:20.5, top:22.3, labelSide:'right' },
      { id:'eug',   label:'Eugene, OR',               left:21.5, top:26.8, labelSide:'right' },
      { id:'phx',   label:'Phoenix, AZ',              left:28.7, top:55.3, labelSide:'right' }
    ])

    const pinStyle = (p) => ({
      left: p.left + '%',
      top: p.top + '%',
      '--pin-color': '#6f2cff',
      '--pin-glow': p.weight === 'lead' ? 1 : 0
    })

    const hoverPin = (i) => {
      const el = pinEls.value[i]
      if (!el) return
      gsap.to(el.querySelector('.pin__pulse'), { scale: 1.25, autoAlpha: 0.55, duration: 0.25, ease: 'power2.out' })
    }
    const leavePin = (i) => {
      const el = pinEls.value[i]
      if (!el) return
      gsap.to(el.querySelector('.pin__pulse'), { scale: 1, autoAlpha: 0.35, duration: 0.25, ease: 'power2.out' })
    }

    const openAction = () => {
      window.open(waHref.value, '_blank', 'noopener')
    }

    const onImageLoad = () => {
      const img = imgRef.value
      if (!img?.naturalWidth) return
      mapNatural.value = { w: img.naturalWidth, h: img.naturalHeight }
    }

    const handleDevClick = (e) => {
      if (!devMode.value) return
      const box = mapRef.value.getBoundingClientRect()
      const left = ((e.clientX - box.left) / box.width) * 100
      const top  = ((e.clientY - box.top ) / box.height) * 100
      lastClick.value = { left, top }
      // eslint-disable-next-line no-console
      console.log(`left: ${left.toFixed(2)}, top: ${top.toFixed(2)}`)
    }

    let ctx
    onMounted(async () => {
      await nextTick()

      // Asegura refs de pines listas
      pinEls.value = pinEls.value.filter(Boolean)

      ctx = gsap.context(() => {
        gsap.set([headRef.value, trustRef.value], { autoAlpha: 0, y: 18 })
        // Pines: etiquetas visibles desde el inicio; solo animamos alpha/scale del pin
        gsap.set(pinEls.value, { autoAlpha: 0, scale: 0.8 })

        gsap.timeline({ scrollTrigger: { trigger: root.value, start: 'top 82%', once: true } })
          .to(headRef.value,  { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' })
          .to(pinEls.value,   { autoAlpha: 1, scale: 1, duration: 0.35, stagger: 0.08, ease: 'back.out(1.7)' }, '-=0.2')
          .to(trustRef.value, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '>-0.1')
      }, root.value)
    })

    onBeforeUnmount(() => { ctx && ctx.revert() })

    return {
      root, headRef, mapRef, imgRef, trustRef,
      mapSrcUs, mapRatio, pinsUs, pinEls,
      pinStyle, hoverPin, leavePin, openAction,
      devMode, lastClick, handleDevClick, onImageLoad,
      waHref, telHref
    }
  }
}
</script>

<style scoped>
.presence{
  background:#0f1117;
  color:#fff;
  padding:48px 16px 56px;
  font-family:'Poppins',system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;
}
.presence__head{ text-align:center; max-width:900px; margin:0 auto 14px; }
.presence__title{ margin:0 0 8px; font-weight:900; font-size:clamp(24px,4.5vw,40px); }
.mark{ background:#6f2cff; padding:2px 8px; border-radius:8px; }
.presence__sub{ margin:0; color:#cfd6e1; font-size:16px; }

/* Mapa */
.map{
  position:relative;
  width:min(1100px, 94vw);
  margin:16px auto 10px;
  border-radius:16px;
  overflow:hidden;
  background:#0b0f19;
  box-shadow: 0 14px 36px rgba(0,0,0,.25);
  border:1px solid #1c2234;
}
.map::before{
  content:"";
  display:block;
  padding-top: var(--ratio, 56.25%);
}
.map__img{
  position:absolute; inset:0; width:100%; height:100%;
  object-fit: contain;
  object-position: center;
  filter: saturate(0.98) contrast(1.05) brightness(0.97);
}

/* Pines (etiquetas SIEMPRE visibles) */
.pin{
  --pin-color:#6f2cff;
  --pin-pulse: rgba(111,44,255,.35);
  position:absolute;
  transform: translate(-50%, -50%);
  background:transparent;
  border:0; padding:0; cursor:pointer;
}
.pin__dot{
  width:12px; height:12px; border-radius:999px;
  background: var(--pin-color);
  box-shadow:
    0 0 0 3px color-mix(in oklab, var(--pin-color) 40%, transparent),
    0 4px 14px rgba(0,0,0,.35),
    0 0 18px rgba(111,44,255, calc(var(--pin-glow,0) * .6));
  display:block;
}
.pin__pulse{
  position:absolute; inset:0;
  width:12px; height:12px; border-radius:999px;
  transform-origin:center;
  background: var(--pin-pulse);
  opacity:.35; filter: blur(6px);
}
.pin__label{
  position:absolute; top:50%; transform: translateY(-50%);
  left:14px; white-space:nowrap;
  background:#121828; border:1px solid #2b3452; color:#eaf0ff;
  padding:6px 10px; border-radius:10px; font-size:12px; font-weight:700;
  pointer-events:none;
  opacity: 1; /* SIEMPRE visible */
}
.pin__label small{ display:block; font-weight:600; font-size:11px; color:#9fb0d5; margin-top:2px; }
.pin__label--left{ left:auto; right:14px; }

/* Trust bar */
.trustbar{
  list-style:none; padding:0; margin:12px auto 0;
  display:flex; gap:12px; flex-wrap:wrap; justify-content:center;
  color:#dbe2ee; font-size:13px;
}
.trustbar li{
  background:#121828; border:1px solid #2b3452; padding:8px 12px; border-radius:999px; font-weight:700;
}

/* CTA */
.presence__cta{
  display:flex; gap:12px; justify-content:center; margin-top:14px;
}
.btn{
  appearance:none; border:0; border-radius:10px; padding:12px 16px; font-weight:800; font-size:14px; cursor:pointer; text-decoration:none;
}
.btn--primary{ background:linear-gradient(180deg,#7a2bff,#5720c9); color:#fff; box-shadow:0 10px 18px rgba(87,32,201,.35); }
.btn--ghost{ background:transparent; color:#e6ebf5; border:1px solid #2b3452; }

/* Dev helpers */
.dev-toggle{
  display:flex; align-items:center; gap:8px; color:#9fb0d5; font-size:13px; justify-content:center; margin-top:10px;
}
.dev-grid{
  position:absolute; inset:0; pointer-events:none;
  background:
    linear-gradient(to right, rgba(255,255,255,.06) 1px, transparent 1px) 0 0/ 5% 100%,
    linear-gradient(to bottom, rgba(255,255,255,.06) 1px, transparent 1px) 0 0/ 100% 5%;
}
.dev-coords{
  position:absolute; right:10px; bottom:10px;
  background:#121828; border:1px solid #2b3452; color:#eaf0ff;
  padding:8px 10px; border-radius:10px; font-size:12px; font-weight:800;
}

/* Responsive */
@media (max-width: 640px){
  .pin__label{ font-size:11px; }
}
</style>
